import { useCallback, useEffect, useState } from 'react';
import {
  getRepositorySummary,
  uploadDocument,
  deleteDocument,
  RepositorySummary,
  GRCDocument,
  downloadDocument,
  saveBlob,
} from '../../../api/grcDocumentApi';
import { fetchCompanyProfile, ContactEntry } from '../../../api/companyProfileApi';
import { DocumentCard } from '../components/DocumentCard';
import { UploadModal, CATEGORY_OPTIONS } from '../components/UploadModal';
import { cached, cacheInvalidate } from '../../../api/apiCache';
import { Eyebrow, PageHeading } from '../../../design-system';
import styles from './AssetRepositoryPage.module.css';

const CATEGORY_SUMMARIES: Record<string, string> = {
  'core-policies': 'Foundation policies required for any GRC programme — AI usage, privacy, ethics, and governance frameworks that define your organisation\'s stance on responsible AI.',
  'compliance-regulatory': 'Evidence of regulatory compliance — compliance maps, DPO appointments, audit results, regulatory filings, and certifications demonstrating adherence to applicable laws.',
  'risk-assessments': 'Risk identification and assessment documents — AI risk registers, impact assessments, bias and fairness testing results, and threat modelling outputs.',
  'data-governance': 'Data management evidence — data inventories, data flow maps, retention schedules, consent records, and data classification schemas.',
  'ai-system-documentation': 'Technical AI documentation — model cards, training data documentation, human oversight procedures, system architecture, and performance monitoring reports.',
  'incident-response': 'Incident preparedness — response plans, incident logs, breach notification procedures, post-mortem reports, and escalation workflows.',
  'contracts-agreements': 'Legal agreements — data processing agreements, vendor contracts, AI licensing terms, NDAs, and third-party risk assessments.',
  'certifications-evidence': 'Certifications and external assessments — ISO 27001/42001, SOC 2, conformity assessments, penetration test reports, and independent audit findings.',
  'accountability': 'Accountability records — governance ownership assignments, RACI matrices, board-level AI oversight minutes, training records, and role-based access reviews.',
};

const GRC_PILLAR_OPTIONS = [
  { value: '', label: 'All Pillars' },
  { value: 'G', label: 'Governance' },
  { value: 'R', label: 'Risk' },
  { value: 'C', label: 'Compliance' },
  { value: 'Hybrid', label: 'Hybrid' },
];

const DOMAIN_OPTIONS = [
  { value: '', label: 'All Domains' },
  { value: 'data-privacy', label: 'Data & Privacy' },
  { value: 'ai-technology', label: 'AI & Technology' },
  { value: 'both', label: 'Both' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'current', label: 'Current' },
  { value: 'outdated', label: 'Outdated' },
  { value: 'pending-review', label: 'Pending Review' },
];

function getCategoryState(uploadedCount: number, expectedCount: number) {
  if (expectedCount === 0) {
    return { tone: 'stateNeutral', label: 'N/A' };
  }
  if (uploadedCount === 0) {
    return { tone: 'stateNotStarted', label: 'Not started' };
  }
  if (uploadedCount >= expectedCount) {
    return { tone: 'stateComplete', label: 'Complete' };
  }
  return { tone: 'stateInProgress', label: 'In progress' };
}

export default function AssetRepositoryPage() {
  const [summary, setSummary] = useState<RepositorySummary | null>(null);
  const [contacts, setContacts] = useState<ContactEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPresetCategory, setUploadPresetCategory] = useState('');
  const [focusedCategory, setFocusedCategory] = useState<string | null>(null);

  // Filters
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPillar, setFilterPillar] = useState('');
  const [filterDomain, setFilterDomain] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Preview / delete state
  const [previewDoc, setPreviewDoc] = useState<GRCDocument | null>(null);
  // The preview is served through the API so PDFs carry the watermark
  // (SRS #37); the presigned URL is only a fallback if that fails.
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  useEffect(() => {
    if (!previewDoc) { setPreviewSrc(null); return; }
    let objectUrl: string | null = null;
    let cancelled = false;
    downloadDocument(previewDoc.id)
      .then(({ blob }) => { if (cancelled) return; objectUrl = URL.createObjectURL(blob); setPreviewSrc(objectUrl); })
      .catch(() => { if (!cancelled) setPreviewSrc(previewDoc.download_url); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [previewDoc]);
  const [deleteTarget, setDeleteTarget] = useState<GRCDocument | null>(null);

  const loadData = useCallback(async (invalidate = false) => {
    if (invalidate) {
      cacheInvalidate('repo-summary');
      cacheInvalidate('company-profile');
    }
    const [summaryResult, profileResult] = await Promise.allSettled([
      cached('repo-summary', 30_000, getRepositorySummary),
      cached('company-profile', 120_000, fetchCompanyProfile),
    ]);
    if (summaryResult.status === 'fulfilled') setSummary(summaryResult.value);
    if (profileResult.status === 'fulfilled') setContacts(profileResult.value.contacts);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleUpload = async (file: File, meta: any) => {
    setUploading(true);
    try {
      await uploadDocument(file, meta);
      await loadData(true);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDocument(deleteTarget.id);
      await loadData(true);
    } catch { /* ignore */ }
    setDeleteTarget(null);
  };

  const openUploadForCategory = (category: string) => {
    setUploadPresetCategory(category);
    setUploadOpen(true);
  };

  const handleView = (doc: GRCDocument) => {
    if (doc.download_url) {
      setPreviewDoc(doc);
    }
  };

  const handleEdit = (doc: GRCDocument) => {
    openUploadForCategory(doc.category);
  };

  const handleReplace = (doc: GRCDocument) => {
    openUploadForCategory(doc.category);
  };

  if (loading) return <div className={styles.loadingPage}>Loading repository...</div>;

  const filteredCategories = (summary?.categories ?? []).filter(cat => {
    if (filterCategory && cat.category !== filterCategory) return false;
    return true;
  });

  // Apply doc-level filters within each category
  const filterDocs = (docs: GRCDocument[]) => {
    return docs.filter(d => {
      if (filterPillar && d.grc_pillar !== filterPillar) return false;
      if (filterDomain && d.domain !== filterDomain) return false;
      if (filterStatus && d.status !== filterStatus) return false;
      return true;
    });
  };

  const totalUploaded = summary?.total_uploaded ?? 0;
  const totalExpected = summary?.total_expected ?? 0;
  const activeFilterCount = [filterCategory, filterPillar, filterDomain, filterStatus].filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0;
  const visibleCategories = focusedCategory
    ? filteredCategories.filter(cat => cat.category === focusedCategory)
    : filteredCategories;
  const visibleDocumentCount = visibleCategories.reduce((count, cat) => count + filterDocs(cat.documents).length, 0);

  const clearFilters = () => {
    setFilterCategory('');
    setFilterPillar('');
    setFilterDomain('');
    setFilterStatus('');
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <Eyebrow tone="default" dot>GRC</Eyebrow>
            <PageHeading
              accentWord="Repository"
              accentTone="orange"
              size="h2"
              align="left"
              level="h1"
            >
              Asset Repository
            </PageHeading>
            <p>Track required evidence and upload documents by category.</p>
          </div>
          <div className={styles.headerActions}>
            <div className={styles.viewToggle}>
              <button type="button" className={`${styles.viewBtn} ${view === 'grid' ? styles.active : ''}`} onClick={() => setView('grid')}>Grid</button>
              <button type="button" className={`${styles.viewBtn} ${view === 'list' ? styles.active : ''}`} onClick={() => setView('list')}>List</button>
            </div>
            <button type="button" className={styles.uploadBtn} onClick={() => openUploadForCategory('')}>+ Upload Document</button>
          </div>
        </div>

        {/* Filters */}
        <div className={styles.filters}>
          <div className={styles.filterControls}>
            <select className={styles.filterSelect} value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
              <option value="">All Categories</option>
              {CATEGORY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <select className={styles.filterSelect} value={filterPillar} onChange={e => setFilterPillar(e.target.value)}>
              {GRC_PILLAR_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <select className={styles.filterSelect} value={filterDomain} onChange={e => setFilterDomain(e.target.value)}>
              {DOMAIN_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
            <select className={styles.filterSelect} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          <div className={styles.filtersMeta}>
            {hasActiveFilters && <span className={styles.activeFilterPill}>{activeFilterCount} filters</span>}
            <span className={styles.resultsCount}>{visibleDocumentCount} docs</span>
            <div className={styles.filtersSummaryCard}>
              <strong>{totalUploaded}/{totalExpected}</strong>
              <span>uploaded</span>
            </div>
            {hasActiveFilters && (
              <button type="button" className={styles.clearFiltersBtn} onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Category sections */}
        {focusedCategory && (
          <div className={styles.focusToolbar}>
            <button
              type="button"
              className={styles.backBtn}
              onClick={() => setFocusedCategory(null)}
            >
              ← Back
            </button>
          </div>
        )}

        <div className={styles.categoriesGrid}>
          {visibleCategories.map(cat => {
            const docs = filterDocs(cat.documents);
            const isExpanded = focusedCategory === cat.category;
            const categoryState = getCategoryState(cat.uploaded_count, cat.expected_count);

            return (
              <div
                key={cat.category}
                className={`${styles.categorySection} ${isExpanded ? styles.categorySectionOpen : ''}`}
              >
                <div className={styles.categoryHeader}>
                  <div className={styles.categoryMain}>
                    <div className={styles.categoryStatusRow}>
                      <span className={`${styles.categoryStateBadge} ${styles[categoryState.tone]}`}>
                        {categoryState.label}
                      </span>
                      <span className={styles.categoryDocCount}>
                        {cat.uploaded_count} {cat.uploaded_count === 1 ? 'document' : 'documents'}
                      </span>
                    </div>

                    <div className={styles.categoryTitleRow}>
                      <span className={styles.categoryName}>{cat.label}</span>
                    </div>

                    <p className={styles.categorySummary}>
                      {CATEGORY_SUMMARIES[cat.category] || ''}
                    </p>
                  </div>

                  <div className={styles.categoryActions}>
                    <button
                      type="button"
                      className={styles.cardViewBtn}
                      onClick={() => setFocusedCategory(isExpanded ? null : cat.category)}
                    >
                      {isExpanded ? 'Back' : 'View all'}
                    </button>
                    <button
                      type="button"
                      className={styles.rowUploadBtn}
                      onClick={() => openUploadForCategory(cat.category)}
                    >
                      Upload
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className={styles.categoryBody}>
                    {docs.length === 0 ? (
                      <div className={styles.emptyState}>No uploaded documents match the current filters.</div>
                    ) : (
                      <div className={view === 'grid' ? styles.docGrid : styles.docList}>
                        {docs.map(doc => (
                          <DocumentCard
                            key={doc.id}
                            doc={doc}
                            view={view}
                            onView={handleView}
                            onEdit={handleEdit}
                            onReplace={handleReplace}
                            onDelete={setDeleteTarget}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {filteredCategories.length === 0 && (
          <div className={styles.emptyState}>No categories match your filters.</div>
        )}
      </div>

      {/* Upload Modal */}
      <UploadModal
        open={uploadOpen}
        onClose={() => {
          setUploadOpen(false);
          setUploadPresetCategory('');
        }}
        onUpload={handleUpload}
        contacts={contacts}
        uploading={uploading}
        initialCategory={uploadPresetCategory}
      />

      {/* PDF preview overlay */}
      {previewDoc && previewDoc.download_url && (
        <div className={styles.previewOverlay} onClick={() => setPreviewDoc(null)}>
          <div className={styles.previewContent} onClick={e => e.stopPropagation()}>
            <div className={styles.previewHeader}>
              <div className={styles.previewHeaderInfo}>
                <h3>{previewDoc.document_name}</h3>
                <span className={styles.previewHeaderMeta}>
                  {previewDoc.original_filename}
                  {previewDoc.file_size_bytes ? ` \u00B7 ${previewDoc.file_size_bytes < 1024 * 1024 ? `${(previewDoc.file_size_bytes / 1024).toFixed(0)} KB` : `${(previewDoc.file_size_bytes / (1024 * 1024)).toFixed(1)} MB`}` : ''}
                </span>
              </div>
              <div className={styles.previewHeaderActions}>
                <button
                  className={styles.previewNewTabBtn}
                  disabled={downloading}
                  onClick={async () => {
                    setDownloading(true);
                    try { const { blob, filename } = await downloadDocument(previewDoc.id); saveBlob(blob, filename); }
                    catch { if (previewDoc.download_url) window.open(previewDoc.download_url, '_blank'); }
                    finally { setDownloading(false); }
                  }}
                >
                  {downloading ? 'Preparing…' : 'Download'}
                </button>
                <button
                  className={styles.previewNewTabBtn}
                  onClick={() => { if (previewSrc) window.open(previewSrc, '_blank'); }}
                >
                  Open in new tab
                </button>
                <button className={styles.closeBtn} onClick={() => setPreviewDoc(null)}>&times;</button>
              </div>
            </div>
            <div className={styles.previewBody}>
              <iframe
                src={previewSrc ?? undefined}
                title={previewDoc.document_name}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className={styles.deleteConfirm}>
          <div className={styles.deleteDialog}>
            <h3>Delete Document?</h3>
            <p>Are you sure you want to delete &quot;{deleteTarget.document_name}&quot;? This action cannot be undone.</p>
            <div className={styles.deleteActions}>
              <button className={styles.cancelBtn} onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className={styles.confirmDeleteBtn} onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
